/** The `lists` area: lists and checklists. Owned by the stage 2 slot views
 *  (docs/I18N.md §11). */
export const lists = {
  // --- The list's columns (`list-columns.ts`) ---------------------------------
  'lists.column.title': 'Title',
  'lists.column.state': 'State',
  'lists.column.health': 'Health',
  'lists.column.assignee': 'Contributor',
  'lists.column.requester': 'Requested by',
  'lists.column.flow': 'Flow',
  'lists.column.parent': 'Parent',
  'lists.column.due': 'Due',
  'lists.column.priority': 'Priority',
  'lists.column.updated': 'Updated',
  'lists.column.created': 'Created',
  /** The priority cell: the stored value, in the reader's words. */
  'lists.cell.priority.must': 'must',
  'lists.cell.priority.should': 'should',
  'lists.cell.priority.could': 'could',

  // --- The table ---------------------------------------------------------------
  /** A column header's menu; `column` is the column's label. */
  'lists.table.columnMenu': '{column} column',
  'lists.table.sortAsc': 'Sort ascending',
  'lists.table.sortDesc': 'Sort descending',
  'lists.table.moveLeft': 'Move left',
  'lists.table.moveRight': 'Move right',
  'lists.table.hideColumn': 'Hide column',
  'lists.table.groupOptions': 'Group options',
  /** A section heading's menu; `group` is the heading. */
  'lists.table.groupOptionsFor': 'Group options: {group}',
  /** A group's end while it holds rows not shown yet (docs/LENSES.md §4a):
   *  how many are left; the press loads them, so does reaching it. */
  'lists.table.more': '{count} more',
  /** The same, named for the reader of a screen; `group` is the heading. */
  'lists.table.moreLabel': 'Show {count} more in {group}',
  'lists.table.retry': 'Not saved. Retry',
  /** The item's list digest after its title (docs/CHECKLIST.md §2). */
  'lists.table.digest': '{done} of {total}',
  'lists.table.digestLabel': 'Its list: {done} of {total} done',
  'lists.table.parents': 'Parents',

  // --- The list surface and its Columns dialog ---------------------------------
  'lists.surface.count': { one: '{count} item', other: '{count} items' },
  'lists.surface.empty': 'Nothing on this board yet.',
  'lists.columns.button': 'Columns',
  /** A row of the Columns dialog, to a screen reader; `column` is its name. */
  'lists.columns.move': 'Move "{column}"',
  'lists.columns.show': 'Show "{column}"',
  'lists.columns.hide': 'Hide "{column}"',

  // --- The checklist -----------------------------------------------------------
  'lists.checklist.addItem': 'Add item',
  'lists.checklist.filterPlaceholder': 'Search or filter…',
  'lists.checklist.filterLabel': 'Search or filter {name}',
  'lists.checklist.tidy': 'Tidy',
  'lists.checklist.tidyLabel': 'Tidy: fold the lines ticked here',
  'lists.checklist.pull': 'Pull to tidy',
  'lists.checklist.release': 'Release to tidy',
  'lists.checklist.unreachable': 'Could not reach the server.',
  'lists.checklist.outside': {
    one: '{count} sub-item lives outside this board: inheritance is off on the way down, so not everyone here could see it.',
    other:
      '{count} sub-items live outside this board: inheritance is off on the way down, so not everyone here could see them.',
  },
  /** A tick or an ask that never reached the server, said on the line. */
  'lists.completion.unreachable': 'Could not reach the server; try again.',

  // --- The checklist's facts column --------------------------------------------
  'lists.aside.thisList': 'This board',
  'lists.aside.progress': '{done} of {total} done',
  'lists.aside.progressClosed': '{done} of {total} done · {closed} closed',
  'lists.aside.progressOutside': '{done} of {total} done · {outside} outside',
  'lists.aside.progressClosedOutside':
    '{done} of {total} done · {closed} closed · {outside} outside',
  'lists.aside.landsLabel': 'New lines land',
  /** Where a typed line lands: under the list's root item, or on the list. */
  'lists.aside.landsUnder': 'under {title}',
  'lists.aside.landsHere': 'on this board',
  'lists.aside.onIt': 'On it',
  'lists.aside.lastChange': 'Last change',
  'lists.aside.hint': 'Select a line to see it here.',
  'lists.aside.close': 'Close',
  'lists.aside.contributor': 'Contributor',
  'lists.aside.requester': 'Requested by',
  'lists.aside.flow': 'Flow',
  'lists.aside.group': { one: 'Group', other: 'Groups' },
  /** Beside a group that reaches the line through a parent item, not through
   *  a grant on the line itself (the access panel's own word). */
  'lists.aside.inherited': 'inherited',
  /** The line's parents within the list. */
  'lists.aside.under': 'Under',
  'lists.aside.health': 'Health',
  'lists.aside.changed': 'Changed',
  'lists.aside.open': 'Open the item',
  /** A row's due label (domain/due.ts `dueLabel`). */
  'lists.due.late': '{count} d late',
  'lists.due.today': 'Due today',
  'lists.due.tomorrow': 'Due tomorrow',
  'lists.due.weekday': 'Due {weekday}',
  'lists.due.day': 'Due {day} {month}',
  'lists.due.dayYear': 'Due {day} {month} {year}',
  'lists.due.week': 'Due W{week}',
  'lists.due.weekYear': 'Due W{week} {year}',
  'lists.due.month': 'Due {month}',
  'lists.due.monthYear': 'Due {month} {year}',
  'lists.due.quarter': 'Due Q{quarter}',
  'lists.due.quarterYear': 'Due Q{quarter} {year}',
  'lists.due.year': 'Due {year}',
  'lists.due.weekdayName.sun': 'Sun',
  'lists.due.weekdayName.mon': 'Mon',
  'lists.due.weekdayName.tue': 'Tue',
  'lists.due.weekdayName.wed': 'Wed',
  'lists.due.weekdayName.thu': 'Thu',
  'lists.due.weekdayName.fri': 'Fri',
  'lists.due.weekdayName.sat': 'Sat',
  'lists.due.monthName.jan': 'Jan',
  'lists.due.monthName.feb': 'Feb',
  'lists.due.monthName.mar': 'Mar',
  'lists.due.monthName.apr': 'Apr',
  'lists.due.monthName.may': 'May',
  'lists.due.monthName.jun': 'Jun',
  'lists.due.monthName.jul': 'Jul',
  'lists.due.monthName.aug': 'Aug',
  'lists.due.monthName.sep': 'Sep',
  'lists.due.monthName.oct': 'Oct',
  'lists.due.monthName.nov': 'Nov',
  'lists.due.monthName.dec': 'Dec',
  'lists.error.needsConfirmation': 'This needs a confirmation the line cannot ask for.',
} as const;
